import React from 'react';

import { Tooltip } from 'antd';

import styled, { css } from 'styled-components';

import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

/**
 * Pulsanti compatti a sfondo pieno per righe e liste dense, dove NwButton è troppo ingombrante.
 * Rispetto a NwButton: la variante è un valore unico invece di booleani che possono contraddirsi
 * (primary + danger + ghost), il loading sostituisce solo l'icona e non la label, quindi la
 * larghezza non salta, e i pulsanti con la sola icona espongono comunque un nome accessibile.
 */

//fill e text sono la versione piena, line e lineText quella con il solo bordo
const VARIANT_COLORS = {
    primary: (theme) => ({
        fill: theme.PrimaryColor,
        text: '#fff',
        line: theme.PrimaryColor,
        lineText: theme.PrimaryColor,
    }),
    danger: (theme) => ({
        fill: theme.DangerColor,
        text: '#fff',
        line: theme.DangerColor,
        lineText: theme.DangerColor,
    }),
    success: (theme) => ({
        fill: theme.SuccessColor,
        text: '#fff',
        line: theme.SuccessColor,
        lineText: theme.SuccessColor,
    }),
    neutral: (theme) => ({
        fill: theme.LighterBorderColor,
        text: theme.TextColor,
        line: theme.DarkerBorderColor,
        lineText: theme.TextColor,
    }),
};

const getVariantColors = (theme, variant) => (VARIANT_COLORS[variant] || VARIANT_COLORS.primary)(theme);

const StyledMiniButton = styled.button`
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5em;
    height: 1.375rem;
    padding: ${(props) => (props.$iconOnly ? '0 0.35rem' : '0 0.5rem')};
    //bordo sempre presente, trasparente nella versione piena: le due versioni restano alte uguali
    border: 1px solid transparent;
    border-radius: 0.25rem;
    font-family: inherit;
    font-size: 0.7rem;
    font-weight: bold;
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    transition:
        filter 0.2s,
        background-color 0.2s;

    ${(props) => {
        const colors = getVariantColors(props.theme, props.$variant);
        if (props.$outlined) {
            return css`
                background-color: transparent;
                border-color: ${colors.line};
                color: ${colors.lineText};

                &:focus-visible {
                    outline: 2px solid ${colors.line};
                    outline-offset: 2px;
                }
            `;
        }
        return css`
            background-color: ${colors.fill};
            color: ${colors.text};

            &:focus-visible {
                outline: 2px solid ${colors.fill};
                outline-offset: 2px;
            }
        `;
    }}

    svg {
        display: block;
        font-size: 1.05em;
        //il glifo e' centrato sul suo viewBox, il testo sul proprio line box che include lo spazio
        //per i discendenti: senza questa correzione ottica l'icona sembra piu' bassa della label
        margin-top: -0.05em;
    }

    .mini-button-label {
        display: block;
        line-height: 1;
    }

    &:hover:enabled,
    &:focus:enabled {
        filter: brightness(0.92);
    }

    &:active:enabled {
        filter: brightness(0.85);
    }

    &:disabled {
        background-color: ${(props) => (props.$outlined ? 'transparent' : props.theme.LighterBorderColor)};
        border-color: ${(props) => (props.$outlined ? props.theme.LighterBorderColor : 'transparent')};
        color: ${(props) => props.theme.DisabledTextColor};
        cursor: ${(props) => (props.$loading ? 'default' : 'not-allowed')};
    }
`;

const NwMiniButton = ({
    label,
    icon,
    variant = 'primary',
    outlined,
    iconOnly,
    loading,
    disabled,
    tooltip,
    type = 'button',
    ...other
}) => {
    const button = (
        <StyledMiniButton
            $variant={variant}
            $outlined={outlined ? true : false}
            $iconOnly={iconOnly ? true : false}
            $loading={loading ? true : false}
            type={type}
            disabled={disabled || loading ? true : false}
            aria-busy={loading ? true : false}
            aria-label={iconOnly ? label || tooltip : undefined}
            {...other}
        >
            {icon && <NwIcon icon={icon} loading={loading} />}
            {!iconOnly && <span className="mini-button-label">{label}</span>}
        </StyledMiniButton>
    );

    if (tooltip) {
        return (
            <Tooltip title={tooltip} mouseEnterDelay={0.4} mouseLeaveDelay={0.2}>
                {button}
            </Tooltip>
        );
    }
    return button;
};

//icone solid: a questa dimensione la famiglia light si perde sullo sfondo pieno
const NwMiniAddButton = ({ label = 'add', icon = byPrefixAndName.fass['plus'], ...other }) => (
    <NwMiniButton label={label} icon={icon} variant="primary" {...other} />
);

const NwMiniRemoveButton = ({ label = 'remove', icon = byPrefixAndName.fass['times'], ...other }) => (
    <NwMiniButton label={label} icon={icon} variant="danger" {...other} />
);

export { NwMiniButton, NwMiniAddButton, NwMiniRemoveButton };
